import React, { useEffect, useRef, useState } from "react";
import { IKO } from "@/lib/design";

/**
 * <EventCard /> — Master Brief Part 4 + Msg 74 item 1.
 *
 * FULL POSTER RULE (unchanged):
 *   4:5 media area, object-fit: contain, blurred+darkened backdrop of the
 *   same poster, never cropped, tap-to-open native aspect.
 *
 * SIZE RULE (Msg 74):
 *   - Whole card fits inside 100dvh minus topbar + bottom nav on 390×844.
 *   - Card NEVER scrolls internally (no overflow:auto).
 *   - Title truncates to 2 lines, venue to 1, no description on the card.
 *   - Type + padding tuned for a glance object, not a document.
 *   - Grid: 1 col <600px, 2 <900, 3 <1200, 4 ≥1200, 5 ≥1600 — handled by the
 *     parent grid.
 */
export function EventCard({ event, onOpen, testId }) {
  const {
    id, title, cover_image_url, iko, price, venue_display, start_at,
    organiser_handle, ticket_link, tags, paid_placement, commission_removed_at,
  } = event || {};
  const [zoom, setZoom] = useState(false);
  const cleanHandle = (organiser_handle || "").replace(/^@+/, "");
  const ikoTone = IKO[iko];

  const openZoom = () => cover_image_url && setZoom(true);

  return (
    <>
      <article
        data-testid={testId || `event-card-${id}`}
        className="group flex flex-col overflow-hidden rounded-xl border"
        style={{
          background: "var(--card-night)",
          borderColor: (paid_placement && !commission_removed_at) ? "var(--kili)" : "var(--line-night)",
          color: "var(--text-night)",
        }}
      >
        {/* Wave 12.1 (Vania 1 Oct) · <span role=button> so there is no nested
            <button> inside this Link-wrapped card. Keyboard a11y preserved. */}
        <span
          role="button"
          tabIndex={0}
          onClick={openZoom}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openZoom(e); } }}
          className="relative block w-full overflow-hidden"
          style={{ aspectRatio: "4 / 5", background: ikoTone?.companion || "#1A1A30", cursor: "zoom-in" }}
          aria-label="open poster"
          data-testid={`event-card-${id}-cover`}
        >
          {cover_image_url ? (
            <>
              <img
                src={cover_image_url}
                alt=""
                aria-hidden
                className="absolute inset-0 h-full w-full"
                style={{
                  objectFit: "cover",
                  filter: "blur(28px) brightness(0.55) saturate(0.9)",
                  transform: "scale(1.15)",
                }}
              />
              <img
                src={cover_image_url}
                alt={title || ""}
                className="relative h-full w-full"
                style={{ objectFit: "contain" }}
                loading="lazy"
              />
            </>
          ) : (
            <div
              className="grid h-full w-full place-items-center text-[11px]"
              style={{
                background: ikoTone?.companion || "#1A1A30",
                color: "var(--text-dim-night)",
                fontFamily: "var(--f-body)",
              }}
            >
              no cover provided
            </div>
          )}
          {/* D28 · advertising placement must never appear on a scored surface.
           If `commission_removed_at` is present the public serialiser has
           already forced `paid_placement=false`, so this render only fires
           on truly-unscored ad slots. */}
        {paid_placement && !commission_removed_at && (
            <span
              className="absolute left-2 top-2 rounded-full px-2 py-0.5 text-[10px] font-semibold"
              style={{ background: "var(--kili)", color: "#1A1A30", fontFamily: "var(--f-head)" }}
            >
              partner
            </span>
          )}
        </span>

        {/* Card meta — compact. No description on the card (Msg 74). */}
        <div className="flex flex-1 flex-col gap-1 p-3" style={{ fontFamily: "var(--f-body)" }}>
          <div className="flex items-center gap-2">
            {iko && (
              <span
                className="rounded-full px-2 py-0.5 text-[10px] leading-none"
                style={{ background: ikoTone?.hex, color: "#1A1A30", fontFamily: "var(--f-head)", fontWeight: 600 }}
              >{ikoTone?.label || iko}</span>
            )}
            {price && (
              <span className="text-[11px] leading-none" style={{ color: "var(--text-muted-night)" }}>{price}</span>
            )}
          </div>
          <h3
            className="lowercase"
            style={{
              fontFamily: "var(--f-head)", fontWeight: 700, letterSpacing: "-0.02em",
              fontSize: 15, lineHeight: 1.15,
              display: "-webkit-box", WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2, overflow: "hidden",
            }}
          >{(title || "").replace(/\s+/g, " ").trim() || "untitled"}</h3>
          <p
            className="text-[11px] leading-tight"
            style={{
              color: "var(--text-muted-night)",
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}
          >{formatWhen(start_at)}{venue_display ? ` · ${venue_display}` : ""}</p>
          {cleanHandle && (
            <p
              className="mt-auto text-[10px]"
              style={{ color: "var(--text-dim-night)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
            >@{cleanHandle}</p>
          )}
        </div>
      </article>

      {/* Full-screen native-aspect zoom on tap */}
      {zoom && cover_image_url && (
        <ZoomOverlay src={cover_image_url} onClose={() => setZoom(false)} />
      )}
    </>
  );
}

function ZoomOverlay({ src, onClose }) {
  const ref = useRef(null);
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div
      data-testid="poster-zoom-overlay"
      role="dialog"
      aria-modal
      className="fixed inset-0 z-[100] grid place-items-center"
      style={{ background: "rgba(18,18,42,0.94)" }}
      onClick={onClose}
    >
      <img
        ref={ref}
        src={src}
        alt="poster"
        style={{ maxHeight: "94dvh", maxWidth: "94vw", objectFit: "contain" }}
      />
      <button
        onClick={onClose}
        className="absolute right-4 top-4 rounded-full px-3 py-1 text-xs"
        style={{ background: "var(--card-night)", color: "var(--text-night)", fontFamily: "var(--f-head)" }}
      >close (esc)</button>
    </div>
  );
}

// WW-162 (Vania 13 Oct 2026 · D-4 QA) · use the shared Nairobi helper so
// a zoneless `start_at` is pinned to EAT BEFORE the formatter runs. A
// California browser previously rendered every zoneless EAT string 10h
// late because `new Date(iso)` parsed it as local-time.
import { nairobiDate, NAIROBI_TZ } from "@/lib/datetime";
function formatWhen(iso) {
  const d = nairobiDate(iso);
  if (!d) return "date TBA";
  return d.toLocaleString("en-GB", {
    timeZone: NAIROBI_TZ,
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
  });
}

export default EventCard;
